import { User, LogOut, ShoppingCart, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useCityNavigation } from '@/hooks/useCityNavigation';

interface MobileMenuProps {
  isOpen: boolean;
  translations: {
    flowers: string;
    gifts: string;
    occasions: string;
    contact: string;
    login: string;
    logout: string;
    profile: string;
    orders: string;
  };
  onClose: () => void;
}

const MobileMenu = ({ isOpen, translations, onClose }: MobileMenuProps) => {
  const { navigateWithCity } = useCityNavigation();
  const { user, signOut } = useAuth();

  const handleAuthAction = () => {
    if (user) {
      signOut();
    } else {
      navigateWithCity('/auth');
    }
    onClose();
  };

  const handleNavigation = (path: string) => {
    navigateWithCity(path);
    onClose();
  };

  const handleLogout = () => {
    signOut();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={onClose}
      />
      
      {/* Mobile Menu */}
      <div 
        id="mobile-menu"
        className="lg:hidden fixed top-0 left-0 right-0 bg-white z-50 shadow-lg border-b border-gray-200 animate-fade-in"
      >
        <div className="container mx-auto px-4 py-4">
          <nav className="flex flex-col space-y-3">
            <button 
              onClick={() => handleNavigation('/flowers')}
              className="text-gray-700 hover:text-rose-600 font-medium text-left py-3 px-2 rounded-lg hover:bg-rose-50 transition-colors duration-200"
            >
              {translations.flowers}
            </button>
            {/* The "Gifts" link used to point at /category/gifts which has
                no products (404). Dropped — categories are reachable from
                /flowers anyway. If you populate a real "gifts" category
                later, restore this link. */}
            <button 
              onClick={() => handleNavigation('/occasion/birthday')}
              className="text-gray-700 hover:text-rose-600 font-medium text-left py-3 px-2 rounded-lg hover:bg-rose-50 transition-colors duration-200"
            >
              {translations.occasions}
            </button>
            <button 
              onClick={() => handleNavigation('/sitemap')}
              className="text-gray-700 hover:text-rose-600 font-medium text-left py-3 px-2 rounded-lg hover:bg-rose-50 transition-colors duration-200"
            >
              {translations.contact}
            </button>
            
            {/* Divider */}
            <div className="border-t border-gray-200 my-2" />
            
            {/* User Authentication Section */}
            {user ? (
              <div className="space-y-2">
                <div className="px-2 py-1">
                  <p className="text-sm text-gray-600">Welcome, {user.email}</p>
                </div>
                <button 
                  onClick={() => handleNavigation('/profile')}
                  className="text-gray-700 hover:text-rose-600 font-medium text-left py-3 px-2 rounded-lg hover:bg-rose-50 transition-colors duration-200 w-full flex items-center"
                >
                  <User className="h-4 w-4 mr-2" />
                  {translations.profile}
                </button>
                <button 
                  onClick={() => handleNavigation('/profile?tab=orders')}
                  className="text-gray-700 hover:text-rose-600 font-medium text-left py-3 px-2 rounded-lg hover:bg-rose-50 transition-colors duration-200 w-full flex items-center"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {translations.orders}
                </button>
                <button 
                  onClick={() => handleNavigation('/profile?tab=occasions')}
                  className="text-gray-700 hover:text-rose-600 font-medium text-left py-3 px-2 rounded-lg hover:bg-rose-50 transition-colors duration-200 w-full flex items-center"
                >
                  <Gift className="h-4 w-4 mr-2" />
                  My Occasions
                </button>
                <button 
                  onClick={handleLogout}
                  className="text-red-600 hover:text-red-700 font-medium text-left py-3 px-2 rounded-lg hover:bg-red-50 transition-colors duration-200 w-full flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {translations.logout}
                </button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                className="self-start w-full justify-start" 
                onClick={handleAuthAction}
              >
                <User className="h-4 w-4 mr-2" style={{ strokeWidth: 1.5 }} />
                {translations.login}
              </Button>
            )}
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;